/**
 * Fold state for the cart drawer's "bought together" block (Jacob, 2026-09-18:
 * "would be good to be able to fold down the köps ofta med section since it
 * blocks the vast majority of the cart").
 *
 * The block is a plain <details open>, so it folds without any JS at all. This
 * script exists only to make the choice STICK, and that is not optional
 * polish: the drawer re-renders from the Section Rendering API after every add
 * and remove, and the server always renders it open, so without this a shopper
 * who folded it would find it open again the moment they added anything --
 * which is exactly when they are most likely to be looking at the cart.
 *
 * Stored per browser in localStorage, never read back by Claude or the server:
 * it is a display preference, the cheapest possible thing to lose. Every access
 * is wrapped, because a private window or blocked site data makes localStorage
 * itself throw, and an upsell block must never be able to break the cart.
 *
 * Two re-entry points, because the drawer comes back in two different ways:
 * replaced wholesale (new nodes -> MutationObserver) or morphed in place
 * (same node, `open` attribute reset to the server's -> the cart event). Both
 * re-apply, so whichever path Horizon takes, the fold survives.
 */
(() => {
  if (window.__jrCartPairs) return;
  window.__jrCartPairs = true;

  const KEY = 'jr:cart-pairs-collapsed';
  const CART_UPDATED = 'shopify:cart:lines-update';

  const isCollapsed = () => {
    try {
      return localStorage.getItem(KEY) === '1';
    } catch {
      return false;
    }
  };

  const remember = (collapsed) => {
    try {
      localStorage.setItem(KEY, collapsed ? '1' : '0');
    } catch {
      /* private window, or site data blocked: fold still works, it just won't stick */
    }
  };

  function apply(el) {
    const collapsed = isCollapsed();
    if (el.open === !collapsed) return;
    // Suppress the toggle this fires, so restoring a state never re-writes it.
    el.__jrRestoring = true;
    el.open = !collapsed;
  }

  function init(el) {
    if (!el.__jrPairsBound) {
      el.__jrPairsBound = true;
      el.addEventListener('toggle', () => {
        if (el.__jrRestoring) {
          el.__jrRestoring = false;
          return;
        }
        remember(!el.open);
      });
    }
    apply(el);
  }

  const scan = (root) => {
    if (root.nodeType !== 1) return;
    if (root.matches?.('.jr-cartpairs')) init(root);
    root.querySelectorAll?.('.jr-cartpairs').forEach(init);
  };

  const start = () => {
    scan(document.body);

    new MutationObserver((records) => {
      for (const record of records) {
        for (const node of record.addedNodes) scan(node);
      }
    }).observe(document.body, { childList: true, subtree: true });

    // Morphed in place: the node survives but its `open` comes back from the
    // server. The event fires before the drawer has re-rendered, so wait a frame.
    document.addEventListener(CART_UPDATED, () => {
      requestAnimationFrame(() => document.querySelectorAll('.jr-cartpairs').forEach(init));
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();

/**
 * Gift picker (jr-cart-pairs.liquid, .jr-cartgift): a click anywhere on a
 * colour card adds it, not only the button (Jacob, 2026-10-06). Delegated on
 * document, because the drawer is re-rendered after every cart change.
 */
(() => {
  if (window.__jrCartGift) return;
  window.__jrCartGift = true;
  document.addEventListener('click', (e) => {
    const card = e.target.closest && e.target.closest('.jr-cartgift__card');
    if (!card || e.target.closest('button, a, input')) return;
    const btn = card.querySelector('.jr-addbtn button, button[type="submit"]');
    if (btn && !btn.disabled) btn.click();
  });
})();

/**
 * Size dropdown on a recommendation card (jr-add-button.liquid, pick_size):
 * the product has several sizes and none is marked "(Standard)", so there is
 * no right size to add for the shopper (Jacob, 2026-10-11). The button stays
 * enabled so it keeps its icon and label; an add with no size chosen is
 * stopped here, in the capture phase, before Horizon's own click handler sees
 * it, and the dropdown is opened instead. Delegated on document, because the
 * drawer is re-rendered after every cart change.
 */
(() => {
  if (window.__jrCartSize) return;
  window.__jrCartSize = true;
  document.addEventListener(
    'click',
    (e) => {
      const btn = e.target.closest && e.target.closest('.jr-addbtn button[type="submit"]');
      if (!btn) return;
      const select = btn.closest('product-form-component')?.querySelector('select.jr-addbtn__size');
      if (!select || select.value) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      select.classList.add('is-needed');
      select.focus();
      try {
        select.showPicker?.();
      } catch {
        /* showPicker needs a user gesture and a visible control; focus alone still points at it */
      }
    },
    true
  );
  document.addEventListener('change', (e) => {
    if (e.target.matches && e.target.matches('select.jr-addbtn__size')) e.target.classList.remove('is-needed');
  });
})();
